/**
 * Lifecycle tables for provisioning runs, steps and browser dispatches.
 *
 * Plan: docs/plans/2026-10-01-2026-refactor-web-04-run-lifecycle-module-plan.md (U1)
 */

import type { ProvisioningRunStatus, ProvisioningStepStatus } from './provisioning'
import type { ProvisioningDispatchStatus } from './provisioning-dispatch'
import { defineRunLifecycle, type RunPhase } from './run-lifecycle'

/**
 * Status-only view of a run. To ask whether a RUN is moving (every poller
 * does), call `provisioningRunPhase(run)` instead: a run's status can say
 * PENDING while nothing will ever advance it.
 *
 * MANUAL_INTERVENTION_REQUIRED is `resting`: the orchestrator moves such a run
 * only when an operator retries or manually completes a step.
 */
export const provisioningRunLifecycle = defineRunLifecycle<ProvisioningRunStatus>({
  phases: {
    PENDING: 'active',
    IN_PROGRESS: 'active',
    SUCCEEDED: 'settled',
    FAILED: 'settled',
    MANUAL_INTERVENTION_REQUIRED: 'resting'
  }
})

/**
 * `retryable` is the backend contract: `retryStep` (orchestrator.ts) rejects
 * anything not FAILED or MANUAL_INTERVENTION_REQUIRED with `INVALID_STATE`, so
 * this list must match it exactly. Offering "Run again" on a SUCCEEDED step
 * produced a button whose only outcome was an error toast.
 *
 * There is no endpoint that runs one PENDING step out of order: the orchestrator
 * advances steps by `stepOrder`, so "do Play before App Store" is not expressible
 * on the API half, only on the browser half, whose lanes are independent.
 */
export const provisioningStepLifecycle = defineRunLifecycle<ProvisioningStepStatus>({
  phases: {
    PENDING: 'active',
    IN_PROGRESS: 'active',
    SUCCEEDED: 'settled',
    FAILED: 'settled',
    SKIPPED: 'settled',
    MANUAL_INTERVENTION_REQUIRED: 'resting',
    MANUALLY_COMPLETED: 'settled'
  },
  retryable: ['FAILED', 'MANUAL_INTERVENTION_REQUIRED']
})

/** A dispatch still owns its browser session while `active`. */
export const provisioningDispatchLifecycle = defineRunLifecycle<ProvisioningDispatchStatus>({
  phases: {
    PENDING: 'active',
    DISPATCHED: 'active',
    RUNNING: 'active',
    SUCCEEDED: 'settled',
    PARTIAL: 'settled',
    FAILED: 'settled',
    TIMED_OUT: 'settled'
  }
})

/**
 * The run's phase, judged from its steps as well as its status: an `active`
 * run with no step `active` is `resting`.
 *
 * A browser-only run (`startRun` with `browserOnly`, orchestrator.ts) seeds
 * every step SKIPPED, stays PENDING and is never advanced, so its status alone
 * would read as in flight forever. The same rule covers an IN_PROGRESS run
 * whose only unsettled step waits on a person.
 */
export function provisioningRunPhase(run: {
  status: ProvisioningRunStatus
  steps: readonly { status: ProvisioningStepStatus }[]
}): RunPhase {
  const phase = provisioningRunLifecycle.phase(run.status)
  if (phase !== 'active') return phase
  return run.steps.some(step => provisioningStepLifecycle.isInFlight(step.status)) ? 'active' : 'resting'
}

/**
 * Whether the UI offers "Mark complete". Narrower than the backend on purpose:
 * `manualCompleteStep` also accepts PENDING and IN_PROGRESS, but offering it on
 * a step in flight would race the orchestrator.
 */
export function canCompleteStepManually(status: ProvisioningStepStatus): boolean {
  return status === 'FAILED' || status === 'MANUAL_INTERVENTION_REQUIRED'
}
